/**
 * Ambient knowledge-graph constellation — the product's signature motif
 * (documents resolving into an interlinked wiki), adapted from the login
 * screen. Use it as an absolutely-positioned background layer inside a
 * `position: relative; overflow: hidden` container (empty states, page
 * headers, hero panels).
 *
 * Deterministic layout (no RNG) so it renders identically everywhere.
 */
const INK = "#0b1020"
const ACCENT = "#ffb454"

const NODES: Array<[number, number]> = [
  [18, 22], [42, 14], [70, 26], [86, 48], [30, 44],
  [56, 40], [74, 62], [22, 70], [48, 74], [64, 86], [38, 90], [88, 78],
]
const EDGES: Array<[number, number]> = [
  [0, 1], [1, 2], [2, 3], [0, 4], [4, 5], [5, 2], [5, 6], [3, 6],
  [4, 7], [7, 8], [8, 5], [8, 9], [9, 6], [7, 10], [10, 8], [9, 11], [6, 11],
]

export interface ConstellationProps {
  /** Overall strength of the motif. `subtle` for backgrounds behind text. */
  intensity?: "subtle" | "full"
  /** Render the radial ink glow behind the graph (login-style hero). */
  glow?: boolean
  /** Extra opacity multiplier applied to the whole layer. */
  opacity?: number
}

export function Constellation({
  intensity = "subtle",
  glow = false,
  opacity = 1,
}: ConstellationProps) {
  const edgeStroke =
    intensity === "full" ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.07)"
  const nodeFill =
    intensity === "full" ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.28)"
  const pulseOpacity = intensity === "full" ? 0.5 : 0.3

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        opacity,
        pointerEvents: "none",
      }}
    >
      {glow && (
        <>
          <defs>
            <radialGradient id="lw-glow" cx="30%" cy="30%" r="80%">
              <stop offset="0%" stopColor="#26305c" />
              <stop offset="100%" stopColor={INK} />
            </radialGradient>
          </defs>
          <rect width="100" height="100" fill="url(#lw-glow)" />
        </>
      )}
      {EDGES.map(([a, b], i) => (
        <line
          key={i}
          x1={NODES[a][0]} y1={NODES[a][1]}
          x2={NODES[b][0]} y2={NODES[b][1]}
          stroke={edgeStroke} strokeWidth={0.4}
        />
      ))}
      {NODES.map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r={i % 4 === 0 ? 1.7 : 1.1} fill={nodeFill} />
          {i % 4 === 0 && (
            <circle
              cx={x} cy={y} r={3.4}
              fill="none" stroke={ACCENT} strokeWidth={0.3}
              opacity={pulseOpacity}
            >
              <animate
                attributeName="r"
                values="2.4;4.2;2.4"
                dur={`${3 + (i % 3)}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values={`${pulseOpacity};0.08;${pulseOpacity}`}
                dur={`${3 + (i % 3)}s`}
                repeatCount="indefinite"
              />
            </circle>
          )}
        </g>
      ))}
    </svg>
  )
}

export default Constellation
